import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatFormField } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { combineLatest, delay, distinctUntilChanged, filter, map, shareReplay, startWith, takeUntil, tap } from 'rxjs';

import { CompositeFormDirective, RmsCompositeFormSubmissionDirective } from '../../../sdk/forms/composite-form';
import { ControlErrorDirective } from '../../../sdk/forms/error';
import { InputMaskDirective } from '../../../sdk/forms/mask';
import { DateValidator } from '../../../sdk/validators/date';
import { DatetimePickerComponent } from '../../date';
import { FilterConfiguration, FilterForm, FilterOperator, FilterType } from '../models/filters.model';
import { OPERATORS_TRANSLATIONS } from '../utils/filters.utils';

@Component({
  selector: 'rms-filter-form',
  imports: [
    ControlErrorDirective,
    DatetimePickerComponent,
    FormsModule,
    InputMaskDirective,
    MatFormField,
    MatInput,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    TranslateModule,
    RmsCompositeFormSubmissionDirective,
    AsyncPipe,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './filter-form.component.html',
  styles: `
    :host {
      @apply w-full min-w-32;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterFormComponent extends CompositeFormDirective<FilterForm> implements OnInit {
  @Input({ required: true }) public config: FilterConfiguration[] = [];
  @Input() public disabledFilters: string[] = [];
  @Input() public specialLabel: string | null = null;
  @Input() public min: DateTime | null = null;
  @Output() public emitFormValue = new EventEmitter<FilterForm>();
  public filterType = FilterType;
  protected form = this.fb.group({
    filter: this.fb.control<string | null>(null, [Validators.required]),
    operator: this.fb.control<FilterOperator | null>(null, [Validators.required]),
    type: this.fb.control<FilterType | null>(null, [Validators.required]),
    value: this.fb.control<string | number | null>({ value: null, disabled: true }, [Validators.required]),
    valueFrom: this.fb.control<DateTime | null>({ value: null, disabled: true }, [
      Validators.required,
      DateValidator.ValidDate,
    ]),
    valueTo: this.fb.control<DateTime | null>({ value: null, disabled: true }, [
      Validators.required,
      DateValidator.ValidDate,
    ]),
  });

  protected currentFilterConfig$ = this.form.controls.filter.valueChanges.pipe(
    filter(Boolean),
    map((filter) => this.config.find((c) => c.filter === filter)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected max = DateTime.now();
  protected FilterType = FilterType;
  protected FilterOperator = FilterOperator;
  protected operatorsTranslations = OPERATORS_TRANSLATIONS;

  private _pendingWriteValue: FilterForm | null = null;

  ngOnInit() {
    super.ngOnInit();

    this.currentFilterConfig$
      .pipe(
        delay(0),
        tap((config) => {
          if (this._pendingWriteValue) {
            const pending = this._pendingWriteValue;
            this._pendingWriteValue = null;
            this.resetFieldsVisibility();
            this.form.patchValue(
              {
                type: pending.type,
                operator: pending.operator,
                value: pending.value,
                valueFrom: pending.valueFrom,
                valueTo: pending.valueTo,
              },
              { emitEvent: false }
            );
            if (pending.type && pending.operator) {
              this.updateFieldsVisibility(pending.type, pending.operator);
            }
            this.form.updateValueAndValidity();
          } else {
            this.form.patchValue({
              type: config ? config.type : null,
              operator: config && config.operators.length > 0 ? config.operators[0] : null,
              value: null,
              valueFrom: null,
              valueTo: null,
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    combineLatest([
      this.form.controls.type.valueChanges.pipe(
        startWith(this.form.controls.type.value),
        filter(Boolean),
        distinctUntilChanged()
      ),
      this.form.controls.operator.valueChanges.pipe(
        startWith(this.form.controls.operator.value),
        filter(Boolean),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        tap(([currentType, currentOperator]) => this.updateFieldsVisibility(currentType, currentOperator)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  override writeValue(form: FilterForm | null): void {
    setTimeout(() => {
      if (!form) {
        const firstAvailableFilter = this.config.find((c) => !this.disabledFilters.includes(c.filter));
        if (firstAvailableFilter) {
          this.form.patchValue({
            filter: firstAvailableFilter.filter,
          });
        }
      } else {
        this._pendingWriteValue = form;
        this.form.patchValue({ filter: form.filter });
      }
    }, 0);
  }

  protected onEmitFormValue(): void {
    const values: FilterForm = {
      filter: this.form.controls.filter.value,
      operator: this.form.controls.operator.value,
      type: this.form.controls.type.value,
      value: this.form.controls.value.value,
      valueFrom: this.form.controls.valueFrom.value,
      valueTo: this.form.controls.valueTo.value,
    };
    this.emitFormValue.emit(values);
  }

  protected numberInputMask = (value: string | number): string => {
    const stringValue = value?.toString() ?? '';

    return stringValue.replace(/\D/g, '');
  };

  protected numberInputUnmask = (value: string): number | null => (!value.length ? null : Number(value));

  private resetFieldsVisibility(): void {
    this.form.controls.value.disable({ emitEvent: false });
    this.form.controls.valueFrom.disable({ emitEvent: false });
    this.form.controls.valueTo.disable({ emitEvent: false });
  }

  private updateFieldsVisibility(currentType: FilterType, currentOperator: FilterOperator): void {
    switch (currentType) {
      case FilterType.TEXT:
      case FilterType.NUMBER:
      case FilterType.SELECT:
        this.form.controls.value.enable({ emitEvent: false });
        this.form.controls.valueFrom.disable({ emitEvent: false });
        this.form.controls.valueTo.disable({ emitEvent: false });
        break;
      case FilterType.DATE:
        this.form.controls.value.disable({ emitEvent: false });

        if (currentOperator === FilterOperator.BEFORE) {
          this.form.controls.valueFrom.disable({ emitEvent: false });
          this.form.controls.valueTo.enable({ emitEvent: false });
        } else if (currentOperator === FilterOperator.AFTER) {
          this.form.controls.valueFrom.enable({ emitEvent: false });
          this.form.controls.valueTo.disable({ emitEvent: false });
        } else if (currentOperator === FilterOperator.BETWEEN) {
          this.form.controls.valueFrom.enable({ emitEvent: false });
          this.form.controls.valueTo.enable({ emitEvent: false });
        }

        break;
      default:
        break;
    }
  }
}
